import React from 'react';
import {
  ContentGenerationItem,
  GetCurrentContentResponse,
  Keyword,
  ParseContentGenerationItem,
  KeywordSection,
  KeywordContent,
  KeywordWeight
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/apiTypes';
import KeywordTag from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/components/KeywordTag';
import { Phrase } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/apiTypes';

type ParsedContent = {
  title: string;
  featureBullets: string[];
  description: string;
};

export const parseAndMergeNewContent = (newContent: ContentGenerationItem): ParseContentGenerationItem => {
  let parsedContent: ParsedContent;
  try {
    parsedContent = JSON.parse(newContent.content);
  } catch (e) {
    console.error('Failed to parse content:', e);
    parsedContent = { title: '', featureBullets: [], description: '' };
  }
  return { ...newContent, content: parsedContent };
};

type ContentKeywordsStructure = {
  weightedKeywordPhrases: {
    sections: KeywordSection[];
  };
};

interface SectionData {
  [key: string]: {
    phrases: KeywordContent[];
    keywords: KeywordWeight[];
  };
}

export function extractSectionData(data: ContentKeywordsStructure): SectionData {
  return data.weightedKeywordPhrases.sections.reduce((acc, section) => {
    acc[section.name] = {
      phrases: section.content,
      keywords: section.groups.flatMap((group) => group.weights)
    };

    return acc;
  }, {} as SectionData);
}

/**
 * Creates an optimistic version of the content with updated phrases while preserving keyword structure
 * Uses two-pointer word-level diff: compares word by word
 * Keeps original phrases with keywords where they match, adds new plain phrases for differences
 */
export const createOptimisticContent = (
  originalContent: KeywordContent[],
  newText: string
): KeywordContent[] => {
  if (!originalContent || originalContent.length === 0) {
    return [];
  }

  // Extract original text
  const originalText = originalContent.map(c => c.phrase).join('');
  
  // If text hasn't changed, return original
  if (originalText === newText) {
    return originalContent;
  }

  // Split new text by words (non-whitespace sequences)
  const newWords = newText.split(/\s+/).filter(w => w.length > 0);
  
  // Split original content into words (maintaining their source phrase)
  const oldWordsWithMetadata: Array<{ word: string; sourceIndex: number; sourcePhrase: KeywordContent }> = [];
  originalContent.forEach((phrase, idx) => {
    const words = phrase.phrase.split(/\s+/).filter(w => w.length > 0);
    words.forEach(word => {
      oldWordsWithMetadata.push({
        word,
        sourceIndex: idx,
        sourcePhrase: phrase
      });
    });
  });

  // Two pointers
  let oldPointer = 0;
  let newPointer = 0;
  
  // Updated array to build
  const updated: KeywordContent[] = [];
  
  // Track current phrase being built
  let currentNewWords: string[] = [];
  let currentOldPhrase: KeywordContent | null = null;
  let currentOldWords: string[] = [];
  
  while (newPointer < newWords.length && oldPointer < oldWordsWithMetadata.length) {
    const newWord = newWords[newPointer];
    const oldWordData = oldWordsWithMetadata[oldPointer];
    
    // Compare words
    if (newWord === oldWordData.word) {
      // Words match!
      
      // If we were building a new phrase, save it first
      if (currentNewWords.length > 0) {
        updated.push({
          phrase: currentNewWords.join(' '),
          groupId: undefined
        });
        currentNewWords = [];
      }
      
      // Check if this is part of the same old phrase we're tracking
      if (currentOldPhrase === null || currentOldPhrase === oldWordData.sourcePhrase) {
        // Continue building this old phrase
        currentOldPhrase = oldWordData.sourcePhrase;
        currentOldWords.push(oldWordData.word);
      } else {
        // Different old phrase - save the previous one and start new
        updated.push({
          ...currentOldPhrase,
          phrase: currentOldWords.join(' ')
        });
        currentOldPhrase = oldWordData.sourcePhrase;
        currentOldWords = [oldWordData.word];
      }
      
      // Increment both pointers
      oldPointer++;
      newPointer++;
    } else {
      // Words don't match - new word is different
      
      // If we were building an old phrase, save it first
      if (currentOldPhrase !== null && currentOldWords.length > 0) {
        updated.push({
          ...currentOldPhrase,
          phrase: currentOldWords.join(' ')
        });
        currentOldPhrase = null;
        currentOldWords = [];
      }
      
      // Add new word to current new phrase
      currentNewWords.push(newWord);
      newPointer++;
    }
  }
  
  // Save any remaining phrase being built - order matters!
  // First save old phrase with keywords (if any)
  if (currentOldPhrase !== null && currentOldWords.length > 0) {
    updated.push({
      ...currentOldPhrase,
      phrase: currentOldWords.join(' ')
    });
  }
  
  // Then save any new words that were being accumulated
  if (currentNewWords.length > 0) {
    updated.push({
      phrase: currentNewWords.join(' '),
      groupId: undefined
    });
  }
  
  // Finally, add any remaining new words as a single phrase
  if (newPointer < newWords.length) {
    const remainingWords = newWords.slice(newPointer);
    updated.push({
      phrase: remainingWords.join(' '),
      groupId: undefined
    });
  }

  return updated;
};

export const processContent = (
  content: {
    phrases: KeywordContent[];
  },
  showKeywords: boolean = true
) => {
  if (!content.phrases || content.phrases.length === 0) {
    return null;
  }

  if (!showKeywords) {
    return content.phrases.map((item) => item.phrase).join(' ');
  }

  const result: React.ReactNode[] = [];

  content.phrases.forEach((item, index) => {
    if (item.groupId) {
      result.push(<KeywordTag key={`phrase-${index}`} label={item.phrase} isEnhanced={item.groupId === 'enhanced'} />);
    } else {
      // Split non-keyword text into individual words for proper wrapping and spacing
      const words = item.phrase?.split(' ').filter(word => word.length > 0) || [];
      words.forEach((word, wordIndex) => {
        result.push(<span key={`text-${index}-${wordIndex}`}>{word}</span>);
      });
    }
  });

  return <>{result}</>;
};

export const exportContentAsCSV = (
  newContent: ParseContentGenerationItem,
  currentContent: GetCurrentContentResponse,
  retailerSku: string,
  retailerName: string,
  categoryName: string,
  subCategoryName: string,
  upc: string,
  brandName: string,
  retailerId: string,
  showSnackbar: (message: string, type: 'success' | 'error') => void
) => {
  if (!newContent || !newContent.content) return;

  try {
    const parsedNewContent =
      typeof newContent.content === 'string' ? JSON.parse(newContent.content) : newContent.content;

    const headers = ['Retailer ID', 'Retailer Name', 'Retailer Sku', 'UPC', 'Brand', 'Category', 'Subcategory'];

    headers.push('Current Title', 'Generated Title');

    // Get max number of bullets from both contents. New and current content may have different number of bullets.
    const currentBulletCount = currentContent?.bullets?.length || 0;
    const newBulletCount = parsedNewContent.featureBullets?.length || 0;
    const maxBullets = Math.max(currentBulletCount, newBulletCount);

    for (let i = 0; i < maxBullets; i++) {
      headers.push(`Current Bullet ${i + 1}`, `Generated Bullet ${i + 1}`);
    }

    headers.push('Current Description', 'Generated Description');

    const row = [
      `"${retailerId}"`,
      `"${retailerName.replace(/"/g, '""')}"`,
      `"${retailerSku ?? ''}"`,
      `"${upc.replace(/"/g, '""')}"`,
      `"${brandName.replace(/"/g, '""')}"`,
      `"${categoryName.replace(/"/g, '""')}"`,
      `"${subCategoryName.replace(/"/g, '""')}"`
    ];

    row.push(
      `"${currentContent?.title?.replace(/"/g, '""') ?? ''}"`,
      `"${parsedNewContent.title?.replace(/"/g, '""') ?? ''}"`
    );

    for (let i = 0; i < maxBullets; i++) {
      const currentBullet = currentContent?.bullets?.[i] ?? '';
      const newBullet = parsedNewContent.featureBullets?.[i] ?? '';
      row.push(`"${currentBullet.replace(/"/g, '""')}"`, `"${newBullet.replace(/"/g, '""')}"`);
    }

    row.push(
      `"${currentContent?.description?.replace(/"/g, '""') ?? ''}"`,
      `"${parsedNewContent.description?.replace(/"/g, '""') ?? ''}"`
    );

    const csvContent = [headers.join(','), row.join(',')].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `content_comparison_${retailerSku}_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error exporting content as CSV:', error);
    showSnackbar('Failed to export content. Please try again later.', 'error');
  }
};

// Temporary CSV export function for the new export API
export const convertExportResponseToCSV = (
  exportData: import('./api/apiTypes').ContentExportItem[],
  showSnackbar: (message: string, type: 'success' | 'error') => void
) => {
  try {
    if (!exportData || exportData.length === 0) {
      showSnackbar('No data to export.', 'error');
      return;
    }

    const headers = [
      'SKU Content Generation ID',
      'Retailer ID', 
      'Retailer SKU',
      'Current Title',
      'Generated Title',
      'Current Description',
      'Generated Description'
    ];

    // Find max number of bullets across all items
    let maxBullets = 0;
    exportData.forEach(item => {
      const currentBulletCount = item.current?.bullets?.length || 0;
      const newBulletCount = item.new?.bullets?.length || 0;
      maxBullets = Math.max(maxBullets, currentBulletCount, newBulletCount);
    });

    // Add bullet headers
    for (let i = 0; i < maxBullets; i++) {
      headers.splice(headers.length - 2, 0, `Current Bullet ${i + 1}`, `Generated Bullet ${i + 1}`);
    }

    const rows = exportData.map(item => {
      const row = [
        `"${item.skuContentGenerationId || ''}"`,
        `"${item.retailerId || ''}"`,
        `"${item.retailerSku || ''}"`,
        `"${item.current?.title?.replace(/"/g, '""') || ''}"`,
        `"${item.new?.title?.replace(/"/g, '""') || ''}"`
      ];

      // Add bullet data
      for (let i = 0; i < maxBullets; i++) {
        const currentBullet = item.current?.bullets?.[i] || '';
        const newBullet = item.new?.bullets?.[i] || '';
        row.push(`"${currentBullet.replace(/"/g, '""')}"`, `"${newBullet.replace(/"/g, '""')}"`);
      }

      // Add descriptions
      row.push(
        `"${item.current?.description?.replace(/"/g, '""') || ''}"`,
        `"${item.new?.description?.replace(/"/g, '""') || ''}"`
      );

      return row.join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');

    // Add UTF-8 BOM for Excel to correctly interpret special characters (em-dashes, etc.)
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `content_export_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showSnackbar('Content exported successfully.', 'success');
  } catch (error) {
    console.error('Error converting export data to CSV:', error);
    showSnackbar('Failed to export content. Please try again later.', 'error');
  }
};
